// Global Instructions Rule Applied!
//
// The bulk review of agent-written short descriptions (owner 2026-11-04:
// signing off ~400 texts one company form at a time is not practical), on
// Configuration → Follower Agents → Company short descriptions. One antd
// Table per tab (To sign off / Signed off) with a checkbox column; the action
// row above it carries the selection count, Clear, and the bulk actions behind
// a Popconfirm that names the count (the bulk-action bar grammar). Each row
// carries the same verbs for one company: Sign off (the ask, primary), Edit
// and Regenerate (bordered). Edit opens an AntD modal (default footer); a
// saved edit makes the text the person's, so it leaves this list.
//
// Regenerate sends the companies 25 at a time (the backend's cap per request)
// with a Progress line and Stop; the agent never replaces a person's text, and
// a declined or failed attempt leaves the old text in place.
import React, { useCallback, useMemo, useRef, useState } from 'react'
import { Button, Input, Modal, Popconfirm, Progress, Table, Tag, Tooltip, Typography } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import dayjs from 'dayjs'
import { toast } from 'react-toastify'
import { showErrorToast } from '../../../core/utils/errorToast.tsx'
import DisabledReason from '../../../core/components/DisabledReason.tsx'
import { getAccessToken } from '../../../core/auth/authService'
import { store } from '../../../state_manager/store.ts'
import {
  regenerateShortDescriptions,
  setCompanyShortDescription,
  signOffShortDescriptions
} from '../../companies/services/shortDescriptionService.ts'
import type {
  AgentWrittenShortDescription,
  CompanyRef
} from '../../../state_manager/data_models/company_short_description.ts'

const { Text } = Typography

const RECORD_LINK_CLASS =
  'cursor-pointer text-blue-600 hover:text-blue-700 hover:underline bg-transparent border-0 p-0 text-left'
const TABLE_SCROLL = { y: 420 }
const DATE_FORMAT = 'MM/DD/YYYY'
const REGENERATE_CHUNK = 25
const LISTED_IN_A_TOAST = 3

const token = () => getAccessToken(store.getState().auth)

/** "A, B and C" (at most three names, then "and N more"). */
const nameList = (rows: CompanyRef[]): string => {
  const names = rows.map((row) => row.accounts__name || 'a company')
  const shown = names.slice(0, LISTED_IN_A_TOAST)
  const more = names.length - shown.length
  return more > 0 ? `${shown.join(', ')} and ${more} more` : shown.join(', ')
}

interface ShortDescriptionsReviewTableProps {
  rows: AgentWrittenShortDescription[]
  maxChars: number
  /** Opens the company form. */
  openCompany: (row: AgentWrittenShortDescription) => void
  /** Called after any change, to reload the list and the tiles. */
  onChanged: () => void | Promise<void>
  empty: string
}

export function ShortDescriptionsReviewTable({
  rows,
  maxChars,
  openCompany,
  onChanged,
  empty
}: ShortDescriptionsReviewTableProps) {
  const [selected, setSelected] = useState<string[]>([])
  const [signingOff, setSigningOff] = useState(false)
  const [regenerating, setRegenerating] = useState<{ done: number; total: number } | null>(null)
  const [editing, setEditing] = useState<AgentWrittenShortDescription | null>(null)
  const [draft, setDraft] = useState('')
  const [saving, setSaving] = useState(false)
  const stopRef = useRef(false)

  const busy = signingOff || regenerating != null
  // a text over the cap is edited or regenerated, never signed off as it is
  const tooLong = useCallback((row: AgentWrittenShortDescription) => row.accounts__short_description.length > maxChars, [maxChars])
  const signableIds = useMemo(
    () =>
      new Set(
        rows.filter((row) => row.prog__status === 'unreviewed' && !tooLong(row)).map((row) => row.accounts__id)
      ),
    [rows, tooLong]
  )
  // a row that left the list (signed off on another tab, edited) leaves the selection too
  const selectedHere = useMemo(() => selected.filter((id) => rows.some((row) => row.accounts__id === id)), [selected, rows])
  const selectedToSignOff = selectedHere.filter((id) => signableIds.has(id))

  const signOff = useCallback(
    async (accountIds: string[]) => {
      setSigningOff(true)
      const res = await signOffShortDescriptions(accountIds, token())
      setSigningOff(false)
      if (res.error || !res.data) {
        showErrorToast(res.error || 'The short descriptions could not be signed off.')
        return
      }
      toast.success(
        res.data.signed_off === 1 ? 'Short description signed off' : `${res.data.signed_off} short descriptions signed off`
      )
      setSelected((prev) => prev.filter((id) => !accountIds.includes(id)))
      await onChanged()
    },
    [onChanged]
  )

  const regenerate = useCallback(
    async (accountIds: string[]) => {
      stopRef.current = false
      setRegenerating({ done: 0, total: accountIds.length })
      const written: CompanyRef[] = []
      const notWritten: CompanyRef[] = []
      try {
        for (let start = 0; start < accountIds.length && !stopRef.current; start += REGENERATE_CHUNK) {
          const chunk = accountIds.slice(start, start + REGENERATE_CHUNK)
          const res = await regenerateShortDescriptions(chunk, token())
          if (res.error || !res.data) {
            showErrorToast(res.error || 'The agent could not write the short descriptions again.')
            break
          }
          written.push(...res.data.written)
          notWritten.push(...res.data.declined, ...res.data.failed)
          setRegenerating({ done: Math.min(start + chunk.length, accountIds.length), total: accountIds.length })
        }
      } finally {
        setRegenerating(null)
        setSelected((prev) => prev.filter((id) => !written.some((row) => row.accounts__id === id)))
        if (written.length > 0) {
          toast.success(
            written.length === 1 ? 'Short description written again' : `${written.length} short descriptions written again`
          )
        }
        if (notWritten.length > 0) {
          const first = notWritten[0]
          showErrorToast(
            `Not written again (the old text stays): ${nameList(notWritten)}.` +
              (first.prog__reason ? ` ${first.accounts__name || 'The first'}: ${first.prog__reason}` : '')
          )
        }
        await onChanged()
      }
    },
    [onChanged]
  )

  const openEdit = (row: AgentWrittenShortDescription) => {
    setEditing(row)
    setDraft(row.accounts__short_description)
  }

  const saveEdit = useCallback(async () => {
    if (!editing) return
    setSaving(true)
    const res = await setCompanyShortDescription(editing.accounts__id, draft, token())
    setSaving(false)
    if (res.error || !res.data) {
      showErrorToast(res.error || 'The short description could not be saved.')
      return
    }
    toast.success('Short description saved')
    setEditing(null)
    await onChanged()
  }, [editing, draft, onChanged])

  const columns: ColumnsType<AgentWrittenShortDescription> = [
    {
      title: 'Company',
      key: 'company',
      width: 200,
      render: (_: unknown, row) => (
        <button type='button' className={RECORD_LINK_CLASS} onClick={() => openCompany(row)}>
          {row.accounts__name || 'Company'}
        </button>
      )
    },
    {
      title: 'Short description',
      key: 'text',
      render: (_: unknown, row) => {
        const text = row.accounts__short_description
        const over = text.length > maxChars
        return (
          <div>
            <span>{text}</span>{' '}
            {over ? (
              <Tooltip title={`${text.length} characters: over the ${maxChars}-character limit. Edit or regenerate it.`}>
                <Tag color='red' className='!mr-0'>
                  Too long
                </Tag>
              </Tooltip>
            ) : text.endsWith('…') ? (
              <Tooltip title='Ends mid-sentence: the agent wrote more and the text was cut to fit. Edit or regenerate it.'>
                <Tag color='gold' className='!mr-0'>
                  Cut off
                </Tag>
              </Tooltip>
            ) : null}
          </div>
        )
      }
    },
    {
      title: 'Written',
      key: 'written',
      width: 100,
      render: (_: unknown, row) => {
        const written = row.accounts__short_description_generated_at
          ? dayjs(row.accounts__short_description_generated_at).format(DATE_FORMAT)
          : '—'
        if (row.prog__status !== 'signed_off') return written
        const by = row.prog__signed_off_by_name ? ` by ${row.prog__signed_off_by_name}` : ''
        const on = row.accounts__short_description_signed_off_at
          ? ` on ${dayjs(row.accounts__short_description_signed_off_at).format(DATE_FORMAT)}`
          : ''
        return <Tooltip title={`Signed off${by}${on}.`}>{written}</Tooltip>
      }
    },
    {
      title: '',
      key: 'actions',
      width: 230,
      render: (_: unknown, row) => (
        <div className='flex gap-1 justify-end'>
          {row.prog__status === 'unreviewed' ? (
            <DisabledReason reason={tooLong(row) ? `Over the ${maxChars}-character limit: edit or regenerate it first.` : null}>
              <Button
                size='small'
                type='primary'
                disabled={busy || tooLong(row)}
                onClick={() => void signOff([row.accounts__id])}
              >
                Sign off
              </Button>
            </DisabledReason>
          ) : null}
          <Button size='small' disabled={busy} onClick={() => openEdit(row)}>
            Edit
          </Button>
          <Popconfirm
            title='Write this short description again?'
            description='The agent writes a new text from the same sources; it replaces this one, unreviewed.'
            okText='Regenerate'
            onConfirm={() => void regenerate([row.accounts__id])}
            disabled={busy}
          >
            <Button size='small' disabled={busy}>
              Regenerate
            </Button>
          </Popconfirm>
        </div>
      )
    }
  ]

  if (rows.length === 0) return <Text type='secondary'>{empty}</Text>

  const draftOver = draft.trim().length > maxChars

  return (
    <div className='space-y-2'>
      <div className='flex items-center gap-2 flex-wrap min-h-[24px]'>
        {regenerating ? (
          <>
            <Progress
              size='small'
              className='!mb-0 w-40'
              percent={Math.round((regenerating.done / Math.max(regenerating.total, 1)) * 100)}
            />
            <Text type='secondary' className='text-xs'>
              Regenerating {regenerating.done} of {regenerating.total}
            </Text>
            <Button size='small' onClick={() => (stopRef.current = true)}>
              Stop
            </Button>
          </>
        ) : selectedHere.length > 0 ? (
          <>
            <Text className='text-xs'>{selectedHere.length} selected</Text>
            <button type='button' className={`${RECORD_LINK_CLASS} text-xs`} onClick={() => setSelected([])}>
              Clear
            </button>
            {selectedToSignOff.length > 0 ? (
              <Popconfirm
                title={`Sign off ${selectedToSignOff.length} short description${selectedToSignOff.length === 1 ? '' : 's'}?`}
                description='Reports print them as reviewed from now on.'
                okText='Sign off'
                onConfirm={() => void signOff(selectedToSignOff)}
                disabled={busy}
              >
                <Button size='small' type='primary' loading={signingOff} disabled={busy}>
                  Sign off selected
                </Button>
              </Popconfirm>
            ) : null}
            <Popconfirm
              title={`Write ${selectedHere.length} short description${selectedHere.length === 1 ? '' : 's'} again?`}
              description='The agent writes new texts from the same sources; each replaces the old one, unreviewed.'
              okText='Regenerate'
              onConfirm={() => void regenerate(selectedHere)}
              disabled={busy}
            >
              <Button size='small' disabled={busy}>
                Regenerate selected
              </Button>
            </Popconfirm>
          </>
        ) : (
          <Text type='secondary' className='text-xs'>
            Tick companies to sign off or regenerate several at once.
          </Text>
        )}
      </div>
      <Table<AgentWrittenShortDescription>
        size='small'
        pagination={false}
        rowKey='accounts__id'
        columns={columns}
        dataSource={rows}
        scroll={TABLE_SCROLL}
        tableLayout='auto'
        rowSelection={{
          selectedRowKeys: selectedHere,
          onChange: (keys) => setSelected(keys.map(String)),
          getCheckboxProps: () => ({ disabled: busy })
        }}
      />
      <Modal
        open={editing != null}
        title={`Short description: ${editing?.accounts__name || 'Company'}`}
        okText='Save'
        okButtonProps={{ disabled: draftOver || draft.trim() === '', loading: saving }}
        onOk={() => void saveEdit()}
        onCancel={() => setEditing(null)}
        destroyOnHidden
      >
        <Input.TextArea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          rows={4}
          count={{ show: true, max: maxChars }}
          status={draftOver ? 'error' : undefined}
        />
        <Text type='secondary' className='text-xs block mt-6'>
          At most {maxChars} characters. Saving makes it your own text: it no longer reads agent-written and needs no
          sign-off.
        </Text>
      </Modal>
    </div>
  )
}

export default ShortDescriptionsReviewTable
